import { useInfiniteQuery } from '@tanstack/react-query';
import { Download, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  ApiError,
  listTrackCards,
  queryKeys,
  type TrackCard,
  type TrackCardQuery,
} from '../../api/index.js';
import { Button } from '../../design-system/button.js';
import { ResourceActionsMenu, SwipeRevealAction } from '../../design-system/resource-actions.js';
import { Banner, StateSurface } from '../../design-system/surfaces.js';
import { usePlaybackTransport, type QueuePlaybackContext } from '../../player/index.js';
import { FavoriteAction } from './favorite-action.js';
import { useTrackDownload } from './track-download.js';
import {
  TrackCardSurface,
  TrackRow,
  playbackItemFromTrack,
  trackTitle,
  useTrackPlaybackAction,
} from './track-presentation.js';
import { useTrackRemoval } from './track-removal.js';

const REMOVAL_BATCH_SIZE = 100;

interface FailedRemoval {
  readonly tracks: readonly TrackCard[];
  readonly removedCount: number;
}

function reportTrackRemovalFailure(error: unknown) {
  console.error('Song Composer catalog removal request failed.', {
    errorName: error instanceof Error ? error.name : 'UnknownError',
    ...(error instanceof ApiError ? { apiStatus: error.status, apiCode: error.code } : {}),
  });
}

function TrackCollectionItem({
  onRemove,
  onStart,
  removalPending,
  selection,
  track,
}: {
  onRemove: () => void;
  onStart: () => Promise<void> | void;
  removalPending: boolean;
  selection?: { readonly selected: boolean; readonly onToggle: () => void };
  track: TrackCard;
}) {
  const playback = useTrackPlaybackAction(track, onStart);
  const download = useTrackDownload(track);
  const title = trackTitle(track);
  const trailing = (
    <div className="flex items-center gap-1">
      <FavoriteAction compact favorite={track.favorite} trackId={track.id} />
      <ResourceActionsMenu
        label={`Actions for ${title}`}
        actions={[
          {
            label: download.label,
            icon: <Download aria-hidden="true" />,
            disabled: download.disabled,
            onSelect: () => void download.download(),
          },
          {
            label: 'Remove from Song Composer',
            icon: <Trash2 aria-hidden="true" />,
            destructive: true,
            disabled: removalPending,
            onSelect: onRemove,
          },
        ]}
      />
    </div>
  );
  return (
    <>
      <TrackRow
        action={playback}
        showProject={false}
        showSeams={false}
        {...(selection ? { selection } : {})}
        track={track}
        trailing={trailing}
      />
      <SwipeRevealAction
        actionAccessibleLabel={`Remove ${title} from Song Composer`}
        actionLabel="Remove"
        className="md:hidden"
        disabled={removalPending || Boolean(selection)}
        onAction={onRemove}
      >
        <TrackCardSurface
          action={playback}
          {...(selection ? { selection } : {})}
          showSeams={false}
          track={track}
          trailing={trailing}
        />
      </SwipeRevealAction>
    </>
  );
}

export function TrackCollection({
  emptyDescription,
  emptyTitle,
  errorTitle,
  loadAll = false,
  onSelectionModeChange,
  playbackContext,
  query,
  selectionScopeKey,
  selectionMode = false,
  transformTracks,
}: {
  emptyDescription: ReactNode;
  emptyTitle: string;
  errorTitle: string;
  loadAll?: boolean;
  onSelectionModeChange?: (active: boolean) => void;
  playbackContext: QueuePlaybackContext;
  query: TrackCardQuery;
  selectionScopeKey?: string | number;
  selectionMode?: boolean;
  transformTracks?: (tracks: readonly TrackCard[]) => readonly TrackCard[];
}) {
  const player = usePlaybackTransport();
  const removal = useTrackRemoval(player.removeTracks);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());
  const [failedRemoval, setFailedRemoval] = useState<FailedRemoval | null>(null);
  const [removalSubmitting, setRemovalSubmitting] = useState(false);
  const collection = useInfiniteQuery({
    queryKey: queryKeys.catalog(query),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listTrackCards({ ...query, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
  const loadedTracks = useMemo(
    () =>
      (collection.data?.pages.flatMap((page) => page.items) ?? []).filter(
        (track) => !removedIds.has(track.id),
      ),
    [collection.data?.pages, removedIds],
  );
  const tracks = useMemo(
    () => (transformTracks ? [...transformTracks(loadedTracks)] : loadedTracks),
    [loadedTracks, transformTracks],
  );
  const selectedTracks = tracks.filter((track) => selectedIds.has(track.id));
  const playbackItems = useMemo(
    () => tracks.flatMap((track) => playbackItemFromTrack(track) ?? []),
    [tracks],
  );

  useEffect(() => {
    setSelectedIds(new Set());
    setFailedRemoval(null);
  }, [query, selectionScopeKey]);
  useEffect(() => {
    if (!selectionMode) setSelectedIds(new Set());
  }, [selectionMode]);
  useEffect(() => {
    if (!loadAll || !collection.hasNextPage || collection.isFetchingNextPage) return;
    void collection.fetchNextPage();
  }, [collection.fetchNextPage, collection.hasNextPage, collection.isFetchingNextPage, loadAll]);

  const startPlayback = async (trackId: string) => {
    const startIndex = playbackItems.findIndex((item) => item.id === trackId);
    if (startIndex < 0) return;
    await player.playQueue(playbackItems, startIndex, playbackContext);
    const completeTracks = [...loadedTracks];
    const seenCursors = new Set<string>();
    let cursor = collection.data?.pages.at(-1)?.next_cursor ?? null;
    try {
      while (cursor && !seenCursors.has(cursor)) {
        seenCursors.add(cursor);
        const page = await listTrackCards({ ...query, cursor });
        completeTracks.push(...page.items.filter((track) => !removedIds.has(track.id)));
        cursor = page.next_cursor;
      }
    } catch {
      return;
    }
    const queueTracks = transformTracks ? transformTracks(completeTracks) : completeTracks;
    player.appendQueue(
      playbackContext.id,
      queueTracks.flatMap((track) => playbackItemFromTrack(track) ?? []),
    );
  };

  const leaveSelectionMode = () => {
    setSelectedIds(new Set());
    onSelectionModeChange?.(false);
  };

  const submitRemoval = async (targetsInput: readonly TrackCard[], previouslyRemovedCount = 0) => {
    if (removalSubmitting) return;
    const targets = [...targetsInput];
    setFailedRemoval(null);
    setRemovalSubmitting(true);
    let removedInThisAttempt = 0;
    try {
      for (let index = 0; index < targets.length; index += REMOVAL_BATCH_SIZE) {
        const batch = targets.slice(index, index + REMOVAL_BATCH_SIZE);
        const trackIds = batch.map((track) => track.id);
        await removal.removeTracks(trackIds);
        setRemovedIds((current) => new Set([...current, ...trackIds]));
        removedInThisAttempt += batch.length;
      }
    } catch (error) {
      reportTrackRemovalFailure(error);
      setFailedRemoval({
        tracks: targets.slice(removedInThisAttempt),
        removedCount: previouslyRemovedCount + removedInThisAttempt,
      });
      return;
    } finally {
      setRemovalSubmitting(false);
    }
    if (selectionMode) leaveSelectionMode();
  };

  const toggleSelection = (trackId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(trackId)) next.delete(trackId);
      else next.add(trackId);
      return next;
    });
  };

  return (
    <>
      {failedRemoval ? (
        <Banner
          action={
            <Button
              disabled={removalSubmitting}
              onClick={() => void submitRemoval(failedRemoval.tracks, failedRemoval.removedCount)}
              size="small"
              variant="ghost"
            >
              {removalSubmitting ? 'Retrying…' : 'Try again'}
            </Button>
          }
          className="mb-4"
          title={
            failedRemoval.tracks.length === 1
              ? 'Track could not be removed'
              : 'Tracks could not be removed'
          }
          tone="danger"
        >
          {failedRemoval.removedCount > 0
            ? `${failedRemoval.removedCount} ${failedRemoval.removedCount === 1 ? 'track was' : 'tracks were'} removed. `
            : 'Nothing changed. '}
          The {failedRemoval.tracks.length === 1 ? 'track remains' : 'tracks remain'} in Song
          Composer.
        </Banner>
      ) : null}

      {collection.isPending ? (
        <StateSurface kind="loading" title="Loading songs" />
      ) : collection.isError ? (
        <StateSurface
          action={
            <Button onClick={() => void collection.refetch()} size="small" variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title={errorTitle}
        />
      ) : tracks.length === 0 && loadAll && collection.hasNextPage ? (
        <StateSurface kind="loading" title="Loading all songs" />
      ) : tracks.length === 0 ? (
        <StateSurface title={emptyTitle}>{emptyDescription}</StateSurface>
      ) : (
        <>
          {selectionMode ? (
            <div className="mb-3 flex min-h-14 flex-wrap items-center justify-between gap-3 rounded-md border border-border-hi bg-bg-2 px-3 py-2">
              <p className="text-sm font-semibold text-ink" role="status">
                {selectedTracks.length} selected
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  disabled={loadAll && Boolean(collection.hasNextPage)}
                  onClick={() =>
                    setSelectedIds(
                      selectedTracks.length === tracks.length
                        ? new Set()
                        : new Set(tracks.map((track) => track.id)),
                    )
                  }
                  size="small"
                  variant="ghost"
                >
                  {loadAll && collection.hasNextPage
                    ? 'Loading all…'
                    : selectedTracks.length === tracks.length
                      ? 'Clear selection'
                      : loadAll
                        ? 'Select all'
                        : 'Select loaded'}
                </Button>
                <Button
                  disabled={selectedTracks.length === 0 || removalSubmitting}
                  onClick={() => void submitRemoval(selectedTracks)}
                  size="small"
                  variant="danger"
                >
                  <Trash2 aria-hidden="true" /> {removalSubmitting ? 'Removing…' : 'Remove'}
                </Button>
              </div>
            </div>
          ) : null}
          <div className="hidden grid-cols-[2.75rem_minmax(12rem,1fr)_auto] gap-4 rounded-t-md border border-border bg-bg-2 px-4 py-2 font-mono text-xs uppercase tracking-[0.1em] text-ink-3 md:grid">
            <span aria-hidden="true" />
            <span>Track</span>
            <span aria-hidden="true" />
          </div>
          <div className="grid gap-2 md:block md:overflow-hidden md:rounded-b-md md:border-x md:border-b md:border-border">
            {tracks.map((track) => (
              <TrackCollectionItem
                key={track.id}
                onRemove={() => void submitRemoval([track])}
                onStart={() => startPlayback(track.id)}
                removalPending={removalSubmitting || removal.isPending}
                {...(selectionMode
                  ? {
                      selection: {
                        selected: selectedIds.has(track.id),
                        onToggle: () => toggleSelection(track.id),
                      },
                    }
                  : {})}
                track={track}
              />
            ))}
          </div>
          {collection.hasNextPage ? (
            <div className="mt-5 flex justify-center">
              {loadAll ? (
                <p className="font-mono text-xs text-ink-3" role="status">
                  Loading all songs…
                </p>
              ) : (
                <Button
                  disabled={collection.isFetchingNextPage}
                  onClick={() => void collection.fetchNextPage()}
                  variant="secondary"
                >
                  {collection.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              )}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
